/**
 * Geofences from FlightBlender (ENG-315) — restricted areas drawn on the Drones
 * and OI maps. `GET /geo_fence_ops/geo_fence?view=…` through the same-origin
 * `/oi-api` proxy as the OI endpoints.
 *
 * Verified live 2026-10-21 (airmarket):
 *   • `view` is LATITUDE first — `swLat,swLng,neLat,neLng`. The ticket's example
 *     is longitude first and returns nothing.
 *   • No bbox size guard (unlike the OI search's 450 km limit), so the caller's
 *     zoom gate is about clutter, not server load.
 *   • Paginated; `page_size` is honoured (500 returned everything) but `pages`
 *     is computed from the default page size, so it can't be trusted.
 *   • The test tenant holds the same record many times over, so duplicates are
 *     collapsed here.
 */
import { config } from '@/lib/config';
import type { OiBbox } from './oiSearch';
import { firstPolygon, validIso } from './oiSearch';
import type { GeoJsonPolygon } from './types';

export type Geofence = {
  id: string;
  name: string;
  polygon: GeoJsonPolygon;
  /** Metres, in `altitude_ref` (WGS84). Null when the API sends none. */
  lower_m: number | null;
  upper_m: number | null;
  altitude_ref: string;
  start_utc: string;
  end_utc: string;
};

/** One `results[]` record, as far as we read it. */
export type RawGeofence = {
  id?: string;
  name?: string | null;
  altitude_ref?: string | null;
  lower_limit?: string | number | null;
  upper_limit?: string | number | null;
  start_datetime?: string | null;
  end_datetime?: string | null;
  raw_geo_fence?: { features?: { geometry?: unknown }[] } | null;
};

type GeofencePage = { results?: RawGeofence[] };

const PAGE_SIZE = 500;
const MAX_PAGES = 4;

function metres(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** One record → a drawable geofence, or null without an id, polygon or parseable
 *  window (it can't be placed or judged active). */
export function toGeofence(r: RawGeofence): Geofence | null {
  const polygon = firstPolygon(r.raw_geo_fence?.features?.[0]?.geometry);
  const start = validIso(r.start_datetime);
  const end = validIso(r.end_datetime);
  if (!r.id || !polygon || !start || !end) return null;
  return {
    id: r.id,
    name: r.name?.trim() || 'Geofence',
    polygon,
    lower_m: metres(r.lower_limit),
    upper_m: metres(r.upper_limit),
    altitude_ref: r.altitude_ref ?? 'WGS84',
    start_utc: start,
    end_utc: end,
  };
}

/** Only geofences in force right now. */
export function isActiveGeofence(g: Geofence, now: number): boolean {
  return Date.parse(g.start_utc) <= now && now < Date.parse(g.end_utc);
}

/** Collapse repeats of the same geofence (same name, window and outline). */
export function dedupeGeofences(list: Geofence[]): Geofence[] {
  const seen = new Set<string>();
  const out: Geofence[] = [];
  for (const g of list) {
    const key = `${g.name}|${g.start_utc}|${g.end_utc}|${JSON.stringify(g.polygon.coordinates)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(g);
  }
  return out;
}

/** The API's `view` value — latitude first. */
export function geofenceView(b: OiBbox): string {
  return `${b.swLat},${b.swLng},${b.neLat},${b.neLng}`;
}

export async function fetchGeofences(bbox: OiBbox): Promise<Geofence[]> {
  const all: Geofence[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const params = new URLSearchParams({
      view: geofenceView(bbox),
      page_size: String(PAGE_SIZE),
      page: String(page),
    });
    const res = await fetch(`${config.oiApi.base}/geo_fence_ops/geo_fence?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(20_000),
    });
    // A page past the end is a 404 ("Invalid page"), not a failure.
    if (res.status === 404 && page > 1) break;
    if (!res.ok) throw new Error(`Geofences error ${res.status}`);
    const body = (await res.json()) as GeofencePage;
    const results = body.results ?? [];
    for (const r of results) {
      const g = toGeofence(r);
      if (g) all.push(g);
    }
    // `pages` is worked out from the default page size, so it overstates the
    // count when `page_size` is raised — a short page is the reliable last page.
    if (results.length < PAGE_SIZE) break;
  }
  return dedupeGeofences(all);
}
